/**
 * Item data processing utilities for kojitsukai item pages
 */

/**
 * Classify item type based on item name
 * @param {string} itemName - Japanese item name
 * @returns {string} Item type classification
 */
export function classifyItemType(itemName) {
  const typeMap = [
    { keywords: ['茶碗'], type: 'tea_bowl' },
    { keywords: ['茶杓'], type: 'tea_scoop' },
    { keywords: ['花入', 'flower vase'], type: 'flower_vase' },
    { keywords: ['香合'], type: 'incense_container' },
    { keywords: ['水指', '水次'], type: 'water_jar' },
    { keywords: ['釜', '風炉'], type: 'kettle' },
    { keywords: ['掛物', '掛軸'], type: 'scroll' }
  ];

  for (const { keywords, type } of typeMap) {
    if (keywords.some(keyword => itemName.includes(keyword))) {
      return type;
    }
  }

  return 'other';
}

/**
 * Generate a unique ID for an item
 * @param {string} itemName - Item name
 * @returns {string} Unique ID
 */
function generateItemId(itemName) {
  // First convert Japanese characters to romaji-like equivalents
  let romanized = itemName
    .replace(/掛物/g, 'kakemono')
    .replace(/幸福/g, 'kofuku')
    .replace(/文/g, 'bun')
    .replace(/曽呂利/g, 'sorori')
    .replace(/新左衛門/g, 'shinzaemon')
    .replace(/花入/g, 'hanaire')
    .replace(/竹/g, 'take')
    .replace(/尺八切/g, 'shakuhachi')
    .replace(/利休/g, 'rikyu')
    .replace(/茶碗/g, 'chawan')
    .replace(/珠光青磁/g, 'juko-seiji')
    .replace(/作/g, 'saku')
    .replace(/宛/g, 'ate')
    .replace(/香合/g, 'kogo')
    .replace(/水指/g, 'mizusashi')
    .replace(/釜/g, 'kama')
    .replace(/風炉/g, 'furo')
    .replace(/棗/g, 'natsume')
    .replace(/蓋置/g, 'futaoki')
    .toLowerCase()
    .replace(/[（）()]/g, '-')  // Replace parentheses with dashes
    .replace(/[→]/g, '-to-')   // Replace arrow with "to"
    .replace(/[『』「」]/g, '-') // Replace quotes with dashes
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

  return romanized.substring(0, 50); // Limit length
}

/**
 * Generate canonical item id by incorporating key person tokens when present.
 * Ensures stable slugs like "rikyu-hanaire" for items with 利休 and 花入.
 */
function generateCanonicalItemId(itemName, connections = []) {
  const base = generateItemId(itemName);

  // If item name already includes a known person token mapping, base is enough
  const hasRikyu = /利休|rikyu/.test(itemName);
  const hashanaire = /花入|hanaire/.test(itemName);

  // Try to infer person from connections when not explicitly in name
  const safeConnections = Array.isArray(connections) ? connections : [];
  const connectionText = safeConnections.map(c => c.person || '').join(' ');
  const connHasRikyu = /利休|Rikyu|Rikyū|Sen no Rikyū/i.test(connectionText);

  // Prefer deterministic order: person-first then item keyword
  if ((hasRikyu || connHasRikyu) && hashanaire) {
    // Ensure rikyu appears first followed by hanaire
    return 'rikyu-hanaire';
  }

  return base;
}

/**
 * Translate item name to English
 * @param {string} itemName - Japanese item name
 * @returns {string} English item name
 */
function translateItemName(itemName) {
  const translations = {
    '茶碗': 'Tea bowl',
    '茶杓': 'Tea scoop',
    '花入': 'Flower vase',
    '香合': 'Incense container',
    '水指': 'Water jar',
    '水次': 'Water vessel',
    '釜': 'Kettle',
    '風炉': 'Brazier',
    '掛物': 'Hanging scroll',
    '掛軸': 'Hanging scroll',
    '羽箒': 'Feather brush',
    '薄器': 'Thin tea container',
    '棗': 'Tea caddy',
    '蓋置': 'Lid rest',
    '建水': 'Waste water bowl',
    '竹': 'bamboo',
    '尺八切': 'shakuhachi cut',
    '珠光青磁': 'Juko celadon',
    '熊川': 'Komagai',
    '織部': 'Oribe',
    '志野': 'Shino',
    '唐津': 'Karatsu',
    '樂': 'Raku'
  };

  let englishName = itemName;
  for (const [jp, en] of Object.entries(translations)) {
    englishName = englishName.replace(new RegExp(jp, 'g'), en);
  }

  // Clean up and format
  englishName = englishName
    .replace(/\s+/g, ' ')
    .replace(/([a-zA-Z])([（])/g, '$1 $2')
    .trim();

  return englishName;
}

/**
 * Create English description for item
 * @param {string} itemName - Item name
 * @param {string} type - Item type
 * @returns {string} English description
 */
function createItemDescription(itemName, type) {
  const typeDescriptions = {
    tea_bowl: 'A ceramic vessel used for drinking tea in the Japanese tea ceremony.',
    tea_scoop: 'A bamboo utensil used to measure and transfer powdered tea.',
    flower_vase: 'A container for displaying flowers in tea ceremony settings.',
    incense_container: 'A decorative container for holding incense.',
    water_jar: 'A ceramic container for holding fresh water during tea ceremony.',
    kettle: 'A metal vessel for heating water for tea preparation.',
    scroll: 'A hanging scroll with calligraphy or artwork for tea room decoration.',
    other: 'A traditional item used in Japanese cultural practices.'
  };

  return typeDescriptions[type] || typeDescriptions.other;
}

/**
 * Build provenance chain from item connections
 * @param {Array} connections - Array of person-item connections
 * @param {Array} personsData - All persons data for lookup
 * @returns {Array} Provenance records
 */
export function buildProvenance(connections, personsData = [], personSlugs = {}) {
  const safeConnections = Array.isArray(connections) ? connections : [];
  return safeConnections.map(connection => {
    // Find the person in the data
    const person = personsData.find(p =>
      p.name_jp === connection.person ||
      p.name_romaji === connection.person ||
      connection.person.includes(p.name_jp) ||
      connection.person.includes(p.name_romaji)
    );

    // Prefer stored person slugs mapping if available
    let personId = null;
    if (person) {
      const displayKey = `${person.name_jp} (${person.name_romaji})`;
      personId = personSlugs[displayKey] || generatePersonId(person);
    } else {
      // Do not fabricate IDs for non-person entities (temples, families, shops)
      personId = null;
    }

    return {
      personId,
      personName: {
        jp: person ? person.name_jp : connection.person,
        en: person ? translatePersonName(person.name_romaji) : connection.person
      },
      relationship: connection.connection,
      period: person ? person.years : undefined
    };
  });
}

/**
 * Generate person ID (same as in personDataProcessing)
 * @param {Object} person - Person data
 * @returns {string} Person ID
 */
function generatePersonId(person) {
  const nameToUse = person.name_romaji || person.name_jp || 'unknown';
  const baseId = nameToUse
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

  const yearSuffix = person.years ? `-${person.years.replace(/[^0-9]/g, '').slice(0, 4)}` : '';
  return `${baseId}${yearSuffix}`;
}

/**
 * Translate person name (same as in personDataProcessing)
 * @param {string} nameRomaji - Romanized name
 * @returns {string} English name
 */
function translatePersonName(nameRomaji) {
  const translations = {
    'Sen no Rikyū': 'Sen no Rikyū',
    'Sen Sōtan': 'Sen Sōtan',
    'Toyotomi Hideyoshi': 'Toyotomi Hideyoshi',
    'Oda Uraku': 'Oda Uraku',
    'Kobori Enshū': 'Kobori Enshū',
    'Masuda Donnō': 'Masuda Donnō',
    'Masuda Reisui-an': 'Masuda Reisui-an',
    'Ii Naosuke': 'Ii Naosuke',
    'Matsudaira Fumai': 'Matsudaira Fumai'
  };

  return translations[nameRomaji] || nameRomaji;
}

/**
 * Transform raw item data into detailed item object
 * @param {string} itemName - Item name
 * @param {Array} connections - Item connections data
 * @param {Array} personsData - All persons data for cross-reference
 * @returns {Object} Transformed item details
 */
export function transformItemData(itemName, connections = [], personsData = [], personSlugs = {}) {
  const itemType = classifyItemType(itemName);
  const itemId = generateCanonicalItemId(itemName, connections);

  // Build provenance chain
  const provenance = buildProvenance(connections, personsData, personSlugs);

  // Build related people from connections
  const safeConnections = Array.isArray(connections) ? connections : [];
  const relatedPeople = safeConnections.map(connection => {
    const person = personsData.find(p =>
      p.name_jp === connection.person ||
      p.name_romaji === connection.person ||
      connection.person.includes(p.name_jp) ||
      connection.person.includes(p.name_romaji)
    );

    // Only include real persons in relatedPeople
    if (!person) return null;

    // Prefer stored person slugs
    const displayKey = `${person.name_jp} (${person.name_romaji})`;
    const resolvedId = (personSlugs && personSlugs[displayKey]) || generatePersonId(person);
    return {
      id: resolvedId,
      name: {
        jp: person.name_jp,
        en: translatePersonName(person.name_romaji)
      },
      years: person.years,
      relationship: connection.connection
    };
  }).filter(Boolean);

  return {
    id: itemId,
    name: {
      jp: itemName,
      en: translateItemName(itemName)
    },
    description: {
      jp: `${itemName}について。詳細は歴史的資料を参照してください。`,
      en: createItemDescription(itemName, itemType)
    },
    type: itemType,
    provenance,
    relatedPeople,
    relatedItems: [] // Will be populated by cross-reference
  };
}

/**
 * Create an item summary object
 * @param {string} itemName - Item name
 * @param {string} relationship - Optional relationship description
 * @returns {Object} Item summary
 */
export function createItemSummary(itemName, relationship = undefined) {
  return {
    id: generateItemId(itemName),
    name: {
      jp: itemName,
      en: translateItemName(itemName)
    },
    type: classifyItemType(itemName),
    relationship
  };
}

/**
 * Validate item data structure
 * @param {Object} item - Item data to validate
 * @returns {Object} Validation result
 */
export function validateItemData(item) {
  const errors = [];

  // Required fields
  if (!item.id) {
    errors.push({
      field: 'id',
      message: 'id is required',
      value: item.id
    });
  }

  if (!item.name || typeof item.name !== 'object') {
    errors.push({
      field: 'name',
      message: 'name is required and must be an object with jp and en properties',
      value: item.name
    });
  } else {
    if (!item.name.jp) {
      errors.push({
        field: 'name.jp',
        message: 'Japanese name is required',
        value: item.name.jp
      });
    }
    if (!item.name.en) {
      errors.push({
        field: 'name.en',
        message: 'English name is required',
        value: item.name.en
      });
    }
  }

  // Validate item type
  const validTypes = ['tea_bowl', 'tea_scoop', 'flower_vase', 'incense_container', 'water_jar', 'kettle', 'scroll', 'other'];
  if (!item.type || !validTypes.includes(item.type)) {
    errors.push({
      field: 'type',
      message: `type must be one of: ${validTypes.join(', ')}`,
      value: item.type
    });
  }

  // Validate arrays
  if (item.provenance && !Array.isArray(item.provenance)) {
    errors.push({
      field: 'provenance',
      message: 'provenance must be an array',
      value: item.provenance
    });
  }

  if (item.relatedPeople && !Array.isArray(item.relatedPeople)) {
    errors.push({
      field: 'relatedPeople',
      message: 'relatedPeople must be an array',
      value: item.relatedPeople
    });
  }

  if (item.relatedItems && !Array.isArray(item.relatedItems)) {
    errors.push({
      field: 'relatedItems',
      message: 'relatedItems must be an array',
      value: item.relatedItems
    });
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}